import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Container, Row, Col } from 'react-bootstrap';
import { injectIntl } from '../../../../../components/Intl/withIntl.js';

import { getProductAttributeList, memoizedGetProducts } from '../../../../Product/ProductActions.js';
import { getUserGroup, getUserGroupRequest } from '../../../../User/UserGroupActions.js';
import { getContentBlockItemsByName, getContentBlockItemValue } from '../../../CmsActions.js';
import { getDefaultLanguage } from '../../../../Intl/IntlHelper.js';

import HtmlComponent from '../../../../../components/Text/HtmlComponent.js';
import Slider from '../../../../../components/Content/Slider.js';
import ProductListItem from '../../../../Product/components/list/ProductListItem.js';

const SLIDER_PER_SLIDE = 4;

function PanelProductList({ dispatch, intl, panel, products = [], userGroup = null, appIsMobile = false }) {
    const groupIdentifier = getContentBlockItemValue(panel.blocks, 'group');

    // Le groupe demandé par l'éditeur n'est dans le store que pour les admins (`user/groups` est une
    // route admin): on le résout à la demande, sans l'appliquer au visiteur (`setOnCurrentUser`).
    useEffect(() => {
        if(groupIdentifier && !userGroup) {
            dispatch(getUserGroupRequest(groupIdentifier, false));
        }
    }, [dispatch, groupIdentifier, userGroup]);

    const title = getContentBlockItemValue(panel.blocks, 'title');
    const content = getContentBlockItemValue(panel.blocks, 'content');
    const viewType = getContentBlockItemValue(panel.blocks, 'viewType') || 'grid';
    const count = parseInt(getContentBlockItemValue(panel.blocks, 'count'), 10) || 4;
    const interval = parseInt(getContentBlockItemValue(panel.blocks, 'interval'), 10) || 7000;

    // Manual product selection wins over the category filter. Each `products` item stores a
    // product _id in `.text`; resolve them against the store list, keeping the editor's order.
    const selectedIds = getContentBlockItemsByName(panel.blocks, 'products').map(item => item.text).filter(id => id);
    const categories = getContentBlockItemsByName(panel.blocks, 'category').map(cat => cat.text).filter(cat => cat);

    let list = [];
    if(selectedIds.length > 0) {
        list = selectedIds.map(id => products.find(product => product._id === id)).filter(product => product);
    } else if(categories.length > 0) {
        list = products.filter(product => [
            ...getProductAttributeList(product, 'categories', intl.locale),
            ...getProductAttributeList(product, 'categories', getDefaultLanguage()),
        ].some(productCategory => categories.map(cat => `${cat}`.toLowerCase()).includes(productCategory.toLowerCase()))).slice(0, count);
    }

    if(list.length === 0) {
        return null;
    }

    const colProps = viewType === 'line' ? { xs: '12' } : { xs: '12', md: '6', lg: '3' };

    const header = (
        <Fragment>
            {title && <h2 className="d-inline-block border-half-warning text-primary">{title}</h2>}
            {content && <HtmlComponent text={content} />}
        </Fragment>
    );

    // Slider mode: horizontal scroll on mobile (like the associated products list), carousel of
    // `SLIDER_PER_SLIDE` products per slide on desktop.
    if(viewType === 'slider') {
        return (
            <Container fluid className="py-2">
                {header}
                {appIsMobile ? (
                    <div className="d-flex flex-nowrap overflow-auto gap-3 pb-2">
                        {list.map(product => (
                            <div key={product._id} className="flex-shrink-0" style={{ width: '230px' }}>
                                <ProductListItem product={product} userGroup={userGroup} viewType="grid" listId="cms-product-list" />
                            </div>
                        ))}
                    </div>
                ) : (
                    <Slider
                        interval={interval}
                        disableControls={list.length <= SLIDER_PER_SLIDE}
                        disableIndicators={list.length <= SLIDER_PER_SLIDE}
                        slides={Array.from({ length: Math.ceil(list.length / SLIDER_PER_SLIDE) }, (unused, slideIndex) => ({
                            content: (
                                <Row className="g-3 justify-content-center px-4 px-md-5">
                                    {list.slice(slideIndex * SLIDER_PER_SLIDE, (slideIndex * SLIDER_PER_SLIDE) + SLIDER_PER_SLIDE).map(product => (
                                        <Col key={product._id} xs="12" md="6" lg="3">
                                            <ProductListItem product={product} userGroup={userGroup} viewType="grid" listId="cms-product-list" />
                                        </Col>
                                    ))}
                                </Row>
                            ),
                        }))}
                    />
                )}
            </Container>
        );
    }

    return (
        <Container fluid className="py-2">
            {header}
            <Row className="gy-3">
                {list.map(product => (
                    <Col key={product._id} {...colProps} className="mb-3">
                        <ProductListItem product={product} userGroup={userGroup} viewType={viewType} listId="cms-product-list" />
                    </Col>
                ))}
            </Row>
        </Container>
    );
}

function mapStateToProps(store, props) {
    const groupIdentifier = getContentBlockItemValue((props.panel || {}).blocks, 'group');
    return {
        products: memoizedGetProducts(store, { isActive: true, isVariation: false }),
        userGroup: getUserGroup(store, groupIdentifier || null),
        appIsMobile: store.app.isMobile,
    };
}

PanelProductList.defaultProps = {
    products: [],
    userGroup: null,
    appIsMobile: false,
};

PanelProductList.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    panel: PropTypes.object.isRequired,
    products: PropTypes.arrayOf(PropTypes.object),
    userGroup: PropTypes.object,
    appIsMobile: PropTypes.bool,
};

export default connect(mapStateToProps)(injectIntl(PanelProductList));
